import { Box, Button, Divider, TextField } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../components/Header";
import { useEffect, useState } from "react";
import { useAuth } from "../../providers/AuthProvider";
import { useNavigate, useParams } from "react-router-dom";
import { UserService } from "../../api/user.service";
import { useAlert } from "../../hooks/useAlert";
import { tokens } from "../../theme";
import { useTheme } from "@mui/material";
import CustomCheckbox from "../../components/CustomCheckbox";
import UploadAvatar from "./UploadAvatar";
import { useTranslation } from "react-i18next";

const UserEdit = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const { userInfo, userToken } = useAuth();
  const showAlert = useAlert();
  const { id } = useParams();
  const [user, setUser] = useState();
  const [isUserUpdated, setIsUserUpdated] = useState(false);

  const handleFormSubmit = (values) => {
    console.log("values:", values);
    updateUser(values);
  };

  // update
  const updateUser = async (data) => {
    try {
      setTimeout(async () => {
        await UserService.update(id, data, userInfo.jwt);
        showAlert(t("Edit User successfully"), "success");
        navigate("/users");
      }, 1500);
    } catch (error) {
      showAlert(t("Edit User error"), "error");
      console.log("Error edit", error);
    }
  };

  useEffect(() => {
    //get data Id
    const fetchUserById = async (id) => {
      const data = await UserService.getById(id);
      setUser(data);
      setIsUserUpdated(false);
    };

    fetchUserById(id);
  }, [id, isUserUpdated]);

  if (!user) return;

  return (
    <Box m="20px">
      <Header title={t("EDIT User")} subtitle={t("Edit User")} />

      <Box
        mt="30px"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{ mb: 5 }}
      >
        <img
          alt="profile-user"
          width="150px"
          height="150px"
          src={
            user.avatarUrl
              ? `${process.env.REACT_APP_STRAPI_URL}${user.avatarUrl}`
              : `../../assets/user.png`
          }
          style={{ cursor: "pointer", borderRadius: "50%", marginBottom: 15 }}
        />

        <UploadAvatar
          userToken={userToken}
          userId={user.id}
          username={user.username}
          avatarUrl={user.avatartUrl}
          setIsUserUpdated={setIsUserUpdated}
        />
      </Box>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={{
          id: user.id,
          username: user.username,
          email: user.email,
          firstName: user?.firstName,
          lastName: user?.lastName,
          phone: user?.phone,
          age: user?.age,
          confirmed: user.confirmed || false,
          blocked: user.blocked || false,
          is2fa: user.is2fa || false,
          isBioAuth: user.isBioAuth || false,
          isVerified: user.isVerified || false,
          isSubChatNotification: user.isSubChatNotification || false,
          // password: "",
          // confirm_password: "",
        }}
        validationSchema={checkoutSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              <TextField
                fullWidth
                disabled
                InputLabelProps={{
                  shrink: true,
                }}
                variant="filled"
                type="text"
                label={t("Id")}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.id}
                name="id"
                error={!!touched.id && !!errors.id}
                helperText={touched.id && errors.id}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                variant="filled"
                color="secondary"
                type="text"
                label={t("Username")}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.username}
                name="username"
                error={!!touched.username && !!errors.username}
                helperText={touched.username && errors.username}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                variant="filled"
                color="secondary"
                type="email"
                label={t("Email")}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.email}
                name="email"
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: "span 2" }}
              />

              <Divider sx={{ gridColumn: "span 4" }}></Divider>

              <TextField
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                variant="filled"
                color="secondary"
                type="text"
                label={t("FirstName")}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values?.firstName}
                name="firstName"
                error={!!touched.firstName && !!errors.firstName}
                helperText={touched.firstName && errors.firstName}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                variant="filled"
                color="secondary"
                type="text"
                label={t("LastName")}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values?.lastName}
                name="lastName"
                error={!!touched.lastName && !!errors.lastName}
                helperText={touched.lastName && errors.lastName}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                variant="filled"
                color="secondary"
                type="text"
                label={t("Phone")}
                placeholder="+380501234567"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values?.phone}
                name="phone"
                error={!!touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                variant="filled"
                color="secondary"
                type="text"
                label={t("Age")}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values?.age}
                name="age"
                error={!!touched.age && !!errors.age}
                helperText={touched.age && errors.age}
                sx={{ gridColumn: "span 2" }}
              />
              <Divider sx={{ gridColumn: "span 4" }}></Divider>

              <CustomCheckbox
                name="confirmed"
                label={t("is Confirmed")}
                colors={colors}
                onChange={handleChange}
                checked={values?.confirmed}
              />

              <CustomCheckbox
                name="blocked"
                label={t("is Blocked")}
                colors={colors}
                onChange={handleChange}
                checked={values?.blocked}
              />

              <CustomCheckbox
                name="is2fa"
                label={t("is 2FA")}
                colors={colors}
                onChange={handleChange}
                checked={values?.is2fa}
              />

              <CustomCheckbox
                name="isBioAuth"
                label={t("is BioAuth")}
                colors={colors}
                onChange={handleChange}
                checked={values?.isBioAuth}
              />

              <CustomCheckbox
                name="isVerified"
                label={t("is Verified")}
                colors={colors}
                onChange={handleChange}
                checked={values?.isVerified}
              />

              <CustomCheckbox
                disabled
                name="isSubChatNotification"
                label={t("is Subscribe Chat Notification")}
                colors={colors}
                onChange={handleChange}
                checked={values?.isSubChatNotification}
              />

              <Divider sx={{ gridColumn: "span 4" }}></Divider>

              {/* <Divider sx={{ gridColumn: "span 4" }}>CHANGE PASSWORD</Divider>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                variant="filled"
                type="password"
                label={t("New password")}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values?.password}
                name="password"
                error={!!touched.password && !!errors.password}
                helperText={touched.password && errors.password}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                variant="filled"
                type="password"
                label={t("Confirm new password")}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values?.confirm_password}
                name="confirm_password"
                error={!!touched.confirm_password && !!errors.confirm_password}
                helperText={touched.confirm_password && errors.confirm_password}
                sx={{ gridColumn: "span 2" }}
              /> */}
            </Box>
            <Box display="flex" justifyContent="end" mt="20px" gap={1}>
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => navigate(-1)}
              >
                {t("Go Back")}
              </Button>
              <Button type="submit" color="secondary" variant="contained">
                {t("Save")}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const checkoutSchema = yup.object().shape({
  username: yup.string().required("required"),
  email: yup.string().required("required"),
  // password: yup.string().min(6, "Password must be 6 characters long"),
  // .matches(/[0-9]/, 'Password requires a number')
  // .matches(/[a-z]/, 'Password requires a lowercase letter')
  // .matches(/[A-Z]/, 'Password requires an uppercase letter')
  // .matches(/[^\w]/, 'Password requires a symbol'),
  // confirm_password: yup
  //   .string()
  //   .oneOf([yup.ref("password"), null], 'Must match "password" field value'),
});

export default UserEdit;
